import { create } from "zustand"

import signImg from "@/assets/imgs/letter/sign.png"
import Toast from "@/components/Toast"
import { i18n } from "@/i18n"
import { getWebcmsProgress } from "@/services/core"

export enum GlobalStage {
    LOADING,
    MAIN,
}

export interface CollectionItem {
    key: string
    ts: number
}

interface States {
    player: null | {
        state: {
            avatar?: string
        }
        collection: null | Record<string, CollectionItem[] | null>
    }
    globalStage: GlobalStage
    loadingDisplayProgress: number
    backgroundMapIdleMotionEnabled: boolean
    percent: number
    letter?: { to: string; contents: string[]; signature: string }
    gameStartTs: number
}
interface Actions {
    setPlayer: (payload: States["player"]) => void
    setGlobalStage: (payload: States["globalStage"]) => void
    setLoadingDisplayProgress: (progress: number) => void
    resetLoadingDisplayProgress: () => void
    setBackgroundMapIdleMotionEnabled: (enabled: boolean) => void
    resetBackgroundMapIdleMotionEnabled: () => void
    fetchMeta: () => Promise<void>
    setGameStartTs: (ts: number) => void
    updateCollectionGroup: (group: string, list: CollectionItem[]) => void
}

export type GlobalStore = States & Actions

export const useGlobalStore = create<GlobalStore>((set) => ({
    player: null,
    setPlayer: (payload) => {
        set({ player: payload })
    },
    globalStage: GlobalStage.LOADING,
    setGlobalStage: (payload) => {
        set({ globalStage: payload })
    },
    loadingDisplayProgress: 0,
    setLoadingDisplayProgress: (progress) => {
        set({ loadingDisplayProgress: Math.min(Math.max(progress, 0), 1) })
    },
    resetLoadingDisplayProgress: () => {
        set({ loadingDisplayProgress: 0 })
    },
    backgroundMapIdleMotionEnabled: true,
    setBackgroundMapIdleMotionEnabled: (enabled) => {
        set({ backgroundMapIdleMotionEnabled: enabled })
    },
    resetBackgroundMapIdleMotionEnabled: () => {
        set({ backgroundMapIdleMotionEnabled: true })
    },
    percent: 0,
    letter: {
        to: "박사에게",
        contents: [
            "이 메시지를 발견했다는 건, 네가 이미 Mon3tr의 기억 영역을 뒤지는 데 상당한 시간을 쏟고 있다는 뜻이겠지. 로도스 아일랜드가 현재 직면한 난제로 인해 그렇게 할 수밖에 없을지도 모르겠지만, 나는 적절한 운동과 충분한 수면을 권장할게…… 기억 속에서 답을 찾는 것보다 그쪽이 훨씬 유익할 테니까.\n이건, 지금 이것을 읽고 있을 네게 아주 작은 위로라도 전하기 위해 남긴 메시지야.\n\n너는 이 이야기를 기억하지 못하겠지.\n이 우주는 한때 찬란한 문명을 꽃피웠어. 그 시대에 지식의 경계는 거의 우주 그 자체와 다름없었지.\n인간이 상상할 수 있는 모든 것이 그대로 인지의 형성 범위가 되었고, 가장 멀리 떨어진 항성조차 책상 위의 서적처럼 손을 뻗으면 닿을 수 있었어.\n그럼에도 불구하고, 사람들은 자신에 관한 의문에 답을 내리지 못하고 있는 상태였지……\n태어날 때부터 생명에게는 어떠한 목적이 존재하는 것일까?\n어쩌면 그건 조물주가 남긴 마지막 장막일지도 몰라.\n의의를 탐구하는 터널에 끝은 없고, 생명은 저마다의 여정 속에서 고독을 향해 나아가도록 정해져 있지.\n\n현자들이나 학자들은 결국 관측 가능한 세계에서 답을 도출하는 것을 포기했고, 우리의 본능에 신념을 맡기는 것을 선택했어.\n왜냐하면 해답이 없는 고독과 혼란 너머에, 사랑이라는 존재가 반드시 있다고 믿을 수밖에 없었기 때문이야.\n아무리 깊은 절망이라 해도, 사람들이 아득한 별의 바다에서 자신이라는 존재가 메아리칠 곳을 찾는 것을 멈추게 할 수는 없었어.",

            "정적이 찾아오기 전까지, 사람들은 수만 광년 너머에 있는 생명의 흔적을 위해 눈물을 흘렸지.\n여정 중에 나는 결국 그 감동을 이해했다고 생각해.\n생명은 아름다워, 우주라는 척도에서 '생명'과 '희망'은 같은 의미를 가지는 거겠지.\n\n과거의 기억을 네게 돌려주는 것은 불가능하기에, 난 이렇게 내 감상을 전하는 것밖에 할 수 없어.\n네 본질을 믿을게. 그러니 너 또한, 스스로 선택한 것들의 의미를 믿어줬으면 해.\n너는 앞으로 무력감을 느끼고 망설임을 느끼게 되겠지. 영원히 너와 함께 걸을 수 있는 이는 없고, 네 고독을 온전히 이해할 수 있는 이도 없을 테니까.\n이제 로도스 아일랜드는 수많은 사람의 안식처야.\n그때가 오더라도, 로도스 아일랜드가 네게 돌아올 장소로 있었으면 좋겠어.\n내 소원, 그것은 네 소원을 지키는 거야.\n이 여정의 끝에서, 누군가가 너와 함께 네가 그토록 바라던 미래를 함께 지켜봐 줄 수 있기를 기도할게.",
        ],
        signature: signImg,
    },
    fetchMeta: async () => {
        const res = await getWebcmsProgress()
        if (res.code === 0) {
            const percent = res.data.results.rows[0]?.percent ?? 0
            set({ percent })
        }
    },
    gameStartTs: 0,
    setGameStartTs: (ts) => {
        set({ gameStartTs: ts })
    },
    updateCollectionGroup: (group, list) => {
        set((state) => {
            if (!state.player) return {}
            return {
                player: {
                    ...state.player,
                    collection: {
                        ...state.player.collection,
                        [group]: list,
                    },
                },
            }
        })
    },
}))

export const selectPlayer = (state: GlobalStore) => state.player
export const selectSetPlayer = (state: GlobalStore) => state.setPlayer
export const selectGlobalStage = (state: GlobalStore) => state.globalStage
export const selectSetGlobalStage = (state: GlobalStore) => state.setGlobalStage

export const usePlayer = () => useGlobalStore(selectPlayer)
export const useSetPlayer = () => useGlobalStore(selectSetPlayer)
export const selectPercent = (state: GlobalStore) => state.percent
export const selectFetchMeta = (state: GlobalStore) => state.fetchMeta
export const selectLetter = (state: GlobalStore) => state.letter

export const useGlobalStage = () => useGlobalStore(selectGlobalStage)
export const useSetGlobalStage = () => useGlobalStore(selectSetGlobalStage)
export const usePercent = () => useGlobalStore(selectPercent)
export const useFetchMeta = () => useGlobalStore(selectFetchMeta)
export const useLetter = () => useGlobalStore(selectLetter)

export const requireLogin = (): boolean => {
    const { player, setGlobalStage } = useGlobalStore.getState()
    if (player) return true
    setGlobalStage(GlobalStage.LOADING)
    Toast.message(i18n.get("msg.require_login"))
    return false
}
